import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Header/Navbar';
import Footer from '../components/Footer';
import AdListGridItem from '../components/AdList/AdListGridItem';
import SbPageHeader from '../components/layout/SbPageHeader';
import SbEmptyState from '../components/layout/SbEmptyState';
import SbInlineSearch from '../components/layout/SbInlineSearch';
import { favoriteListingService } from 'api/services/FavoriteListingService';
import { listingViewModelToAdListing } from 'api/utils/listingMapper';
import type { AdListing } from 'types/adListing';
import '@fortawesome/fontawesome-free/css/all.min.css';

const Favoriler: React.FC = () => {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<AdListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchFavorites = useCallback(async () => {
    if (!user?.id) {
      setFavorites([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setFetchError(null);
    try {
      const result = await favoriteListingService.getByUserId(user.id);
      if (result.isSuccess && result.data) {
        const list = result.data
          .filter((f) => f.listing != null)
          .map((f) =>
            listingViewModelToAdListing({ ...f.listing!, isUserFavorite: true })
          );
        setFavorites(list);
      } else {
        setFavorites([]);
        setFetchError(result.message ?? 'Favoriler yüklenemedi.');
      }
    } catch (err) {
      setFavorites([]);
      setFetchError(err instanceof Error ? err.message : 'Bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  const toggleFavorite = async (listingId: string | number, e: React.MouseEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    const result = await favoriteListingService.toggle({
      userId: user.id,
      listingId: String(listingId),
    });
    if (result.isSuccess) {
      setFavorites((prev) => prev.filter((item) => item.id !== listingId));
    }
  };

  const filteredFavorites = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return favorites;
    return favorites.filter(
      (item) =>
        item.baslik?.toLowerCase().includes(q) ||
        item.aciklama?.toLowerCase().includes(q)
    );
  }, [favorites, searchQuery]);

  return (
    <div className="min-vh-100 d-flex flex-column" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
      <Navbar />

      <main className="sb-page-container py-4 sb-page-shell flex-grow-1">
        <SbPageHeader
          title="Favori İlanlarım"
          subtitle="Kaydettiğiniz ilanları buradan takip edin."
          backTo={{ to: '/', label: 'Ana Sayfaya Dön' }}
        />

        {fetchError && (
          <div className="alert alert-warning mb-3" role="alert">
            {fetchError}
          </div>
        )}

        {!loading && favorites.length > 0 && (
          <SbInlineSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Favorilerimde ara..."
            id="favorites-search"
          />
        )}

        {loading ? (
          <div className="text-center py-5 text-muted">
            <div className="spinner-border text-primary mb-2" role="status" />
            <p>Favoriler yükleniyor...</p>
          </div>
        ) : filteredFavorites.length > 0 ? (
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-4 g-4">
            {filteredFavorites.map((item) => (
              <AdListGridItem
                key={item.id}
                ilan={item}
                detailLinkBase="/ilan-detay-login"
                onToggleFavorite={toggleFavorite}
              />
            ))}
          </div>
        ) : favorites.length > 0 ? (
          <SbEmptyState
            iconClass="fas fa-search"
            title="Eşleşen favori bulunamadı"
            description="Arama teriminizi değiştirmeyi deneyin."
          />
        ) : (
          <SbEmptyState
            iconClass="far fa-heart"
            title="Henüz favori ilanınız yok"
            description="Beğendiğiniz ilanları favorilerinize ekleyerek daha sonra kolayca bulabilirsiniz."
            actionLabel="İlanlara Göz At"
            actionTo="/"
          />
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Favoriler;
